import { Cancel, CheckCircle } from "@mui/icons-material";
import { Box, Chip, TableCell, TableRow } from "@mui/material";
import React from "react";
import { t } from "ttag";
import AuditTableShell from "./AuditTableShell";
import useStyles from "./EdgeAudits.style";
import { getDeviceConnections } from "./edgeAuditsApi";
import { formatDurationMs, formatElapsedSince, formatMillisShort, formatTs, yesNo } from "./edgeAuditsUtils";
import { usePagedAudits } from "./usePagedAudits";

const COLUMN_COUNT = 12;

/** CM-730: one row per EDGE connection session, newest first. */
const ConnectionsTable = ({ deviceId, refreshKey = 0 }: { deviceId: string; refreshKey?: number }) => {
  const classes = useStyles();
  const paged = usePagedAudits(getDeviceConnections, deviceId, refreshKey);

  const renderDisconnectedAt = (audit: any) => {
    if (!audit.disconnectionTs) {
      return <Chip label={t`Open`} size="small" color="success" variant="outlined" />;
    }
    return formatTs(audit.disconnectionTs);
  };

  const renderDuration = (audit: any) => {
    if (!audit.disconnectionTs) {
      return formatElapsedSince(audit.connectionTs);
    }
    return formatDurationMs(audit.durationMs);
  };

  const renderHandshake = (audit: any) => (
    <Box display="flex" alignItems="center">
      {audit.handshakeCompleted ? (
        <CheckCircle className={classes.okIcon} titleAccess={t`Handshake completed`} />
      ) : (
        <Cancel className={classes.failIcon} titleAccess={t`Handshake not completed`} />
      )}
      <span>{formatMillisShort(audit.handshakeMs)}</span>
    </Box>
  );

  const header = (
    <TableRow>
      <TableCell className={classes.headCell}>{t`CONNECTED AT`}</TableCell>
      <TableCell className={classes.headCell}>{t`DISCONNECTED AT`}</TableCell>
      <TableCell className={classes.headCell}>{t`DURATION`}</TableCell>
      <TableCell className={classes.headCell}>{t`DISCONNECT REASON`}</TableCell>
      <TableCell className={classes.headCell}>{t`HANDSHAKE`}</TableCell>
      <TableCell className={classes.headCell}>{t`ACTIONS`}</TableCell>
      <TableCell className={classes.headCell}>{t`FIRMWARE`}</TableCell>
      <TableCell className={classes.headCell}>{t`BUILD DATE`}</TableCell>
      <TableCell className={classes.headCell}>{t`ITC`}</TableCell>
      <TableCell className={classes.headCell}>{t`MTLS`}</TableCell>
      <TableCell className={classes.headCell}>{t`REGISTERED`}</TableCell>
      <TableCell className={classes.headCell}>{t`DM INSTANCE`}</TableCell>
    </TableRow>
  );

  return (
    <AuditTableShell
      header={header}
      rowCount={paged.rows.length}
      colSpan={COLUMN_COUNT}
      emptyText={t`No connection audits for this device yet.`}
      loading={paged.loading}
      totalCount={paged.totalCount}
      page={paged.page}
      pageSize={paged.pageSize}
      onPageChange={paged.handlePageChange}
      onPageSizeChange={paged.handlePageSizeChange}
    >
      {paged.rows.map((audit: any) => (
        <TableRow hover key={audit.id} className={classes.row}>
          <TableCell className={classes.cell}>{formatTs(audit.connectionTs)}</TableCell>
          <TableCell className={classes.cell}>{renderDisconnectedAt(audit)}</TableCell>
          <TableCell className={classes.cell}>{renderDuration(audit)}</TableCell>
          <TableCell className={classes.cell}>{audit.disconnectReason || "-"}</TableCell>
          <TableCell className={classes.cell}>{renderHandshake(audit)}</TableCell>
          <TableCell className={classes.cell}>{yesNo(audit.haveActions)}</TableCell>
          <TableCell className={classes.cell}>{audit.firmwareVersion || "-"}</TableCell>
          <TableCell className={classes.cell}>{audit.buildDate || "-"}</TableCell>
          <TableCell className={classes.numCell}>{audit.itcVersion ?? "-"}</TableCell>
          <TableCell className={classes.cell}>{yesNo(audit.mtls)}</TableCell>
          <TableCell className={classes.cell}>{yesNo(audit.isRegistered)}</TableCell>
          <TableCell className={classes.cell}>{audit.dmid || "-"}</TableCell>
        </TableRow>
      ))}
    </AuditTableShell>
  );
};

export default ConnectionsTable;
